import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

const Reports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const buildingParamInit = searchParams.get("building");
  const periodParamInit = searchParams.get("period");
  const searchParamInit = searchParams.get("search") || "";
  const filteredParamInit = searchParams.get("filtered") === "1";

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(searchParamInit);
  const [error, setError] = useState("");

  // Filter states
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(
    buildingParamInit ? { value: buildingParamInit, label: "Loading..." } : null
  );
  const [selectedPeriod, setSelectedPeriod] = useState(
    periodParamInit ? { value: periodParamInit, label: "Loading..." } : null
  );
  const [hasSearched, setHasSearched] = useState(filteredParamInit);
  const [filteredKeys, setFilteredKeys] = useState([]);

  const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);

  // Sync filter state to URL params
  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (selectedBuilding) nextParams.set("building", String(selectedBuilding.value));
    if (selectedPeriod) nextParams.set("period", String(selectedPeriod.value));
    if (search.trim()) nextParams.set("search", search.trim());
    if (hasSearched) nextParams.set("filtered", "1");
    const currentQuery = searchParams.toString();
    const nextQuery = nextParams.toString();
    if (currentQuery !== nextQuery) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [selectedBuilding, selectedPeriod, search, hasSearched, searchParams, setSearchParams]);

  useEffect(() => {
    fetchAvailableOptions();
    if (!filteredParamInit) fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPeriodsForBuilding = async (buildingId) => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}building/${buildingId}/periods`, {
      headers: {
        "api-key": process.env.REACT_APP_API_KEY,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      }
    });
    if (res.data.status !== "success") return [];
    const periods = res.data.data.periods || [];
    return periods.map(period => ({
      value: period.id,
      label: `${new Date(period.start_date).toLocaleDateString()} - ${new Date(period.end_date).toLocaleDateString()}`
    }));
  };

  const restoreFromUrlParams = async (buildingOptions) => {
    const matchedBuilding = buildingOptions.find(b => String(b.value) === String(buildingParamInit));
    if (!matchedBuilding) return;
    setSelectedBuilding(matchedBuilding);
    try {
      const periodOptions = await fetchPeriodsForBuilding(matchedBuilding.value);
      setAvailablePeriods(periodOptions);
      const matchedPeriod = periodParamInit
        ? periodOptions.find(p => String(p.value) === String(periodParamInit)) || null
        : null;
      if (matchedPeriod) setSelectedPeriod(matchedPeriod);
      if (filteredParamInit) {
        setHasSearched(true);
        const fetchedKeys = await fetchKeys(matchedBuilding.value, matchedPeriod?.value || null);
        setFilteredKeys(fetchedKeys);
      }
    } catch (err) {
      console.error("Error restoring filters from URL:", err);
    }
  };

  // Fetch available buildings for filters
  const fetchAvailableOptions = async () => {
    try {
      const buildingsRes = await axios.get(`${process.env.REACT_APP_API_URL}building/list`, {
        headers: {
          "api-key": process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        }
      });

      if (buildingsRes.data.status === "success") {
        const buildings = buildingsRes.data.data.buildings || [];
        const buildingOptions = buildings.map(building => ({
          value: building.id,
          label: building.building_name
        }));
        setAvailableBuildings(buildingOptions);
        if (buildingParamInit) await restoreFromUrlParams(buildingOptions);
      }
    } catch (err) {
      console.error("Error fetching available options:", err);
    }
  };

  const fetchKeys = async (buildingId = null, periodId = null) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (buildingId) params.building_id = buildingId;
      if (periodId) params.period_id = periodId;

      const res = await axios.get(`${process.env.REACT_APP_API_URL}reports/keys`, {
        headers: {
          "api-key": process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params,
      });

      if (res.data.status === "success") {
        const fetchedKeys = res.data.data || [];
        setKeys(fetchedKeys);
        return fetchedKeys;
      } else {
        setError(res.data.message || "Unable to load reports.");
        return [];
      }
    } catch (err) {
      console.error("Reports list error:", err);
      setError(err?.response?.data?.message || err.message || "Unable to load reports.");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle building selection and load periods
  const handleBuildingChange = async (buildingOption) => {
    setSelectedBuilding(buildingOption);
    setSelectedPeriod(null);
    if (!buildingOption) {
      setAvailablePeriods([]);
      return;
    }
    try {
      const periodOptions = await fetchPeriodsForBuilding(buildingOption.value);
      setAvailablePeriods(periodOptions);
    } catch (err) {
      console.error("Error fetching periods:", err);
      setAvailablePeriods([]);
    }
  };

  // Fetch filtered keys
  const fetchFilteredKeys = async () => {
    if (!selectedBuilding) {
      alert("Please select a building");
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      
      const fetchedKeys = await fetchKeys(selectedBuilding.value, selectedPeriod?.value || null);
      setFilteredKeys(fetchedKeys);
    } catch (err) {
      console.error("Error filtering keys:", err);
      setFilteredKeys([]);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to view all reports page
  const handleViewReports = () => {
    if (!selectedBuilding) {
      alert("Please select a building and apply filters first");
      return;
    }

    const params = new URLSearchParams({
      building_id: selectedBuilding.value,
      returnTo,
    });

    if (selectedPeriod) {
      params.append('period_id', selectedPeriod.value);
    }

    navigate(`/reports/all?${params.toString()}`);
  };

  const searchFilteredKeys = useMemo(() => {
    const keysToFilter = hasSearched ? filteredKeys : keys;
    if (!search.trim()) return keysToFilter;
    const term = search.trim().toLowerCase();
    return keysToFilter.filter((item) => {
      const keyName = item.key_name?.toLowerCase() || "";
      const buildingName = item.building_name?.toLowerCase() || "";
      return (
        keyName.includes(term) ||
        buildingName.includes(term) ||
        String(item.id).includes(term)
      );
    });
  }, [keys, filteredKeys, search, hasSearched]);

  const formatPeriod = (item) => {
    if (!item.start_date && !item.end_date) return "-";
    try {
      const start = item.start_date ? new Date(item.start_date).toLocaleDateString() : "-";
      const end = item.end_date ? new Date(item.end_date).toLocaleDateString() : "-";
      return `${start} - ${end}`;
    } catch {
      return "-";
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <h1 className="page-section-title mb-0">Reports</h1>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search by key, building or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <button className="btn btn-outline-secondary" onClick={() => fetchKeys()} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="key-card mb-4 p-lg-4 p-3 mt-4">
        <h5 className="mb-3">Filters</h5>
        <div className="row">
          <div className="col-md-2">
            <div className="form-group">
              <label htmlFor="building">Building</label>
              <div className="custom-select">
                <Select
                  options={availableBuildings}
                  value={selectedBuilding}
                  onChange={handleBuildingChange}
                  placeholder="Select building"
                  isClearable
                />
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label htmlFor="period">Time Period</label>
              <div className="custom-select">
                <Select
                  options={availablePeriods}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  placeholder="Select time period (optional)"
                  isDisabled={!selectedBuilding}
                  isClearable
                />
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={fetchFilteredKeys}
                disabled={!selectedBuilding || loading}
              >
                Filter
              </button>
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={() => {
                  setSelectedBuilding(null);
                  setSelectedPeriod(null);
                  setAvailablePeriods([]);
                  setHasSearched(false);
                  setFilteredKeys([]);
                  fetchKeys();
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Reports Button */}
      {hasSearched && filteredKeys.length > 0 && (
        <div className="key-card p-3 p-lg-4 mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">
                {filteredKeys.length} {filteredKeys.length === 1 ? 'Key' : 'Keys'} Found
              </h5>
              <p className="text-muted mb-0">
                Building: {selectedBuilding?.label}
                {selectedPeriod && ` | Period: ${selectedPeriod.label}`}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={handleViewReports}
            >
              View All Reports
            </button>
          </div>
        </div>
      )}

      {/* Keys List Table */}
      <div className="key-card p-3 p-lg-4">
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 mb-0">Loading keys...</p>
          </div>
        ) : searchFilteredKeys.length === 0 ? (
          <div className="text-center py-5">
            <p className="mb-2">No keys found for reporting.</p>
            <p className="text-muted mb-0">Try adjusting your filters or refresh the list.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>ID</th>
                  <th style={{ width: 90 }}>Order</th>
                  <th>Key Name</th>
                  <th>Building</th>
                  <th>Period</th>
                  <th>Destinations</th>
                  <th>Costs</th>
                  <th style={{ width: 140 }} className="text-end">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {searchFilteredKeys.map((item) => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td>{item.key_order ?? "-"}</td>
                    <td>{item.key_name || "-"}</td>
                    <td>{item.building_name || "-"}</td>
                    <td>{formatPeriod(item)}</td>
                    <td>{item.destination_count || 0}</td>
                    <td>{item.cost_count || 0}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/reports/${item.id}?returnTo=${returnTo}`)}
                      >
                        View Report
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;

